import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ValidationHandlerService, TranslatedToastrService, ImgWithSrcComponentFixWidth75px, BaseComponent, LanguageService, DataTableRequest, OneColumnSearchData, FileResponse } from 'lib/Vestra.shared';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService, CoreService, ImportFileSaveModel } from 'src/app/services/api-services';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import { Router } from '@angular/router';

@Component({
    selector: 'app-language-list',
    templateUrl: './language-list.component.html',
    styleUrls: ['./language-list.component.scss'],
})
export class LanguageListComponent extends BaseComponent implements OnInit, AfterViewInit {
    @ViewChild('imgWidth75pxRef') imgWidth75pxRef: TemplateRef<ImgWithSrcComponentFixWidth75px>;

    constructor(protected override validationHandlerService: ValidationHandlerService, private translatedToastrService: TranslatedToastrService, private translateService: TranslateService, protected override modalService: BsModalService, private coreService: CoreService, private authenticationService: AuthenticationService, protected override spinnerService: NgxSpinnerService, protected override languageService: LanguageService, private router: Router) {
        super(spinnerService, modalService, validationHandlerService, translateService, languageService);
    }

    private importFile: File;
    public fileName: string = '';

    public override getDtOptions() {
        this.languageUrl = this.languageService.getTranslationUrl();

        const res: ADTSettings = {
            serverSide: true,
            processing: false,
            pageLength: 20,
            lengthChange: false,
            responsive: {
                details: {
                    type: 'column',
                    target: -1,
                },
            },
            searching: false,
            destroy: true,
            language: {
                url: this.languageUrl,
            },
            ajax: (dataTablesParams: any, callback: any) => {
                const searchData: OneColumnSearchData = {
                    propertyValue: this.searchValue,
                };

                for (let order of dataTablesParams.order) {
                    order.name = dataTablesParams.columns[order.column]?.data;
                }

                const model: DataTableRequest = {
                    draw: dataTablesParams.draw,
                    start: dataTablesParams.start,
                    length: dataTablesParams.length,
                    order: dataTablesParams.order,
                    search: searchData,
                    columns: this.transformToColumnData(this.dtOptions.columns),
                };

                this.datatableRequest = model;

                this.handleSpinner(firstValueFrom(this.coreService.getLanguageListDataSource(model)))
                    .then(res => {
                        callback(this.getTableData(res));
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                        callback(this.getEmptyTableData());
                    });
            },
            columns: [
                {
                    data: 'name',
                    title: 'common.name',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'code',
                    title: 'common.code',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'shortCode',
                    title: 'common.short_code',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'flagImageUrl',
                    title: 'common.image',
                    responsivePriority: 1,
                    searchable: false,
                    orderable: false,
                    ngTemplateRef: {
                        ref: this.imgWidth75pxRef,
                    },
                },
                {
                    data: 'isDefault',
                    title: 'common.is_default',
                    searchable: false,
                    orderable: true,
                    render: function (data: any, type: any, row: any, meta: any) {
                        const isChecked = data === true ? 'checked' : '';

                        return `
                            <input type="checkbox" class="form-check-input" disabled ${isChecked}>
                        `;
                    },
                },
                {
                    data: '',
                    orderable: false,
                    responsivePriority: 1,
                    render: function (data) {
                        return '';
                    },
                },
            ],
            columnDefs: [
                {
                    targets: '_all',
                    visible: true,
                },
                {
                    className: 'details-icon dtr-control',
                    orderable: false,
                    target: -1,
                },
            ],
        };

        return res;
    }

    public ngOnInit(): void {
        this.dataTableOnInit();
    }

    public ngAfterViewInit() {
        this.dataTableAfterViewInit();
    }

    public ngOnDestroy() {
        this.dataTableOnDestroy();
    }
    public onSearch(event: string): void {
        this.searchValue = event;
        this.refreshDataTable();
    }

    public onImportLanguage = async (): Promise<any> => {
        if (this.importFile != null) {
            let itemToSave: ImportFileSaveModel = {
                importFile: <any>this.importFile,
            };
            return this.handleSpinner(
                firstValueFrom(this.coreService.importResourceFromFile(itemToSave))
                    .then(res => {
                        this.clearFileInput();
                        return Promise.resolve(res);
                    })
                    .catch(err => {
                        this.validationHandlerService.handleServerError(err);
                    })
            );
        } else {
            this.translatedToastrService.warning('common.file_is_required', 'common.warning');
            return;
        }
    };

    public onExportLanguage = async (): Promise<any> => {
        return this.handleSpinner(
            firstValueFrom(this.coreService.getLanguageExport())
                .then(res => {
                    this.downloadFile(res);
                })
                .catch(err => {
                    this.validationHandlerService.handleServerError(err);
                })
        );
    };

    public downloadFile(fileResponse: FileResponse) {
        const blob = fileResponse.data;
        const fileName = fileResponse.fileName || 'download.xlsx';

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
    }

    public onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.importFile = input.files[0];
            this.onImportLanguage();
        }
    }

    private clearFileInput() {
        this.setEmptyFileInputValue('import-file-input');
    }
}
